import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface TechnicalSentimentsProps {
  isRightPanelVisible?: boolean;
}

const TechnicalSentiments: React.FC<TechnicalSentimentsProps> = ({ 
  isRightPanelVisible = false 
}) => {
  const { theme } = useTheme();
  const [sentiment, setSentiment] = useState<'Sell' | 'Neutral' | 'Buy'>('Neutral');
  const [sentimentValue, setSentimentValue] = useState(50); // 0-100 scale

  // Simulate changing sentiment (in real app, this would come from API)
  useEffect(() => {
    const interval = setInterval(() => {
      const randomValue = Math.random() * 100;
      setSentimentValue(randomValue);
      
      if (randomValue <= 33) {
        setSentiment('Sell');
      } else if (randomValue <= 66) {
        setSentiment('Neutral');
      } else {
        setSentiment('Buy');
      }
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Calculate angle for the needle (90-0 degrees, staying above horizontal)
  const needleAngle = 90 - (sentimentValue / 100) * 90;

  // Get color based on sentiment
  const getSentimentColor = () => {
    switch (sentiment) {
      case 'Sell': return '#ef4444'; // red-500
      case 'Neutral': return '#6b7280'; // gray-500
      case 'Buy': return '#22c55e'; // green-500
      default: return '#6b7280';
    }
  };

  return (
    <div className="py-2 pt-0">
      <div className={`theme-bg-secondary bg-opacity-95 p-3 py-3 rounded-lg z-10 shadow-xs relative ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}`}>
        {/* Header */}
        {/* Half Circle Gauge */}
        <div className="relative w-full h-36 flex justify-center items-center mb-0">
          <svg width="200" height="100" className="absolute top-[15px]">
            {/* Define gradient */}
            <defs>
              <linearGradient id="sentimentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="50%" stopColor="#6b7280" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
            </defs>
            
            {/* Background arc */}
            <path
              d="M 30 85 A 70 70 0 0 1 170 85"
              fill="none"
              stroke={theme === 'dark' ? '#374151' : '#e5e7eb'}
              strokeWidth="6"
              strokeLinecap="round"
            />
            
            {/* Gradient arc from red (left) to green (right) */}
            <path
              d="M 30 85 A 70 70 0 0 1 170 85"
              fill="none"
              stroke="#6b7280"
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                stroke: 'url(#sentimentGradient)',
                strokeWidth: '6',
                strokeLinecap: 'round'
              }}
            />
            
            {/* Needle */}
            <g transform={`rotate(${needleAngle} 100 80)`}>
              <line
                x1="100"
                y1="80"
                x2="100"
                y2="35"
                stroke={getSentimentColor()}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="100"
                cy="80"
                r="3"
                fill={getSentimentColor()}
              />
            </g>
          </svg>

          {/* Three labels positioned around the perfect 180-degree circle */}
          {/* Sell at 180° (left) */}
          <div className="absolute left-2 bottom-2">
            <div className="text-xs font-medium text-gray-500">Sell</div>
          </div>
          
          {/* Neutral at 90° (top) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0">
            <div className="text-xs font-medium text-gray-500">Neutral</div>
          </div>
          
          {/* Buy at 0° (right) */}
          <div className="absolute right-2 bottom-2">
            <div className="text-xs font-medium text-gray-500">Buy</div>
          </div>
        </div>

        {/* Current Sentiment */}
        <div className="text-center -mt-5">
          <div 
            className="text-xs font-bold"
            style={{ color: getSentimentColor() }}
          >
            {sentiment}
          </div>
          <div className="text-xs theme-text-quaternary mt-1">
            Weekly Strength • {sentimentValue.toFixed(0)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalSentiments;
